import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Shield, ArrowRight } from "lucide-react";
import { toast } from "sonner";

// Lazily inject Razorpay's checkout.js once, and resolve when window.Razorpay is ready.
function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    s.onload = () => resolve(!!window.Razorpay);
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });
}

export default function Checkout() {
  const { bookingId } = useParams();
  const nav = useNavigate();
  const [booking, setBooking] = useState(null);
  const [paying, setPaying] = useState(false);
  const [rzpEnabled, setRzpEnabled] = useState(false);
  const [billing, setBilling] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("tablo_billing") || '{"company_name":"","gstin":"","billing_address":""}'); }
    catch (_) { return { company_name: "", gstin: "", billing_address: "" }; }
  });
  const [forCompany, setForCompany] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [b, cfg] = await Promise.all([
          api.get(`/bookings/${bookingId}`),
          api.get(`/payments/razorpay/config`).catch(() => ({ data: { enabled: false } })),
        ]);
        setBooking(b.data);
        setRzpEnabled(!!cfg.data.enabled);
      } catch (e) {
        toast.error("Booking not found");
        nav("/home");
      }
    })();
  }, [bookingId]); // eslint-disable-line

  const pay = async () => {
    if (forCompany) {
      if (!billing.gstin || billing.gstin.length !== 15) {
        toast.error("Enter a valid 15-character GSTIN");
        return;
      }
      if (!billing.company_name.trim()) {
        toast.error("Enter the company name");
        return;
      }
      sessionStorage.setItem("tablo_billing", JSON.stringify(billing));
    }
    setPaying(true);
    try {
      if (rzpEnabled) {
        const ok = await loadRazorpay();
        if (!ok) throw new Error("Couldn't load Razorpay. Check your connection.");
        const { data: order } = await api.post("/payments/razorpay/create-order", { booking_id: bookingId });
        await new Promise((resolve, reject) => {
          const rzp = new window.Razorpay({
            key: order.key_id,
            amount: order.amount_paise,
            currency: order.currency,
            order_id: order.order_id,
            name: "Tablo",
            description: `Deposit for booking ${bookingId.slice(-6)}`,
            theme: { color: "#1f1f1f" },
            prefill: order.prefill || {},
            handler: async (res) => {
              try {
                await api.post("/payments/razorpay/verify", {
                  booking_id: bookingId,
                  razorpay_order_id: res.razorpay_order_id,
                  razorpay_payment_id: res.razorpay_payment_id,
                  razorpay_signature: res.razorpay_signature,
                });
                resolve();
              } catch (err) { reject(err); }
            },
            modal: {
              ondismiss: () => reject(new Error("Payment cancelled")),
            },
          });
          rzp.on("payment.failed", (resp) => reject(new Error(resp?.error?.description || "Payment failed")));
          rzp.open();
        });
      } else {
        // Dev fallback when keys aren't configured
        await new Promise((r) => setTimeout(r, 1200));
        await api.post("/payments/mock-deposit", { booking_id: bookingId });
      }
      toast.success("Deposit captured. Booking confirmed!");
      nav(`/confirmation/${bookingId}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Payment failed");
    } finally {
      setPaying(false);
    }
  };

  if (!booking) return <div className="py-10 text-light-ink animate-pulse">Loading...</div>;

  return (
    <div className="space-y-6 animate-fade-up max-w-lg mx-auto">
      <header>
        <p className="text-amber text-xs uppercase tracking-[0.18em] font-medium mb-2">Confirm with deposit</p>
        <h1 className="font-serif text-3xl">A 10% deposit locks your booking.</h1>
        <p className="text-light-ink text-sm mt-2">GST 18% applies on the booking total. Balance is paid at the venue.</p>
      </header>

      {(() => {
        const base = (booking.deposit_amount_inr || 0) + (booking.balance_amount_inr || 0);
        const gst = Math.round(base * 0.18);
        const grand = base + gst;
        const depositWithGst = Math.round((booking.deposit_amount_inr || 0) * 1.18);
        return (
          <div className="tablo-card p-5 space-y-3" data-testid="checkout-summary">
            <div className="flex justify-between"><span className="text-light-ink">Restaurant</span><span className="font-medium">{booking.restaurant?.name}</span></div>
            <div className="flex justify-between"><span className="text-light-ink">Guests</span><span className="font-medium">{booking.pax}</span></div>
            <div className="flex justify-between"><span className="text-light-ink">Date / Time</span><span className="font-medium">{booking.date} · {booking.time}</span></div>
            {booking.package && (
              <div className="flex justify-between"><span className="text-light-ink">Package</span><span className="font-medium">{booking.package.name} · ₹{booking.package.price_per_head_inr}/head</span></div>
            )}
            <hr className="border-tablo-border my-2"/>
            <div className="flex justify-between"><span className="text-light-ink">Booking subtotal</span><span className="font-medium">₹{base.toLocaleString("en-IN")}</span></div>
            <div className="flex justify-between"><span className="text-light-ink">GST @ 18%</span><span className="font-medium">₹{gst.toLocaleString("en-IN")}</span></div>
            <div className="flex justify-between text-sm border-t border-tablo-border pt-2"><span className="text-light-ink">Grand total (incl. GST)</span><span className="font-medium">₹{grand.toLocaleString("en-IN")}</span></div>
            <div className="flex justify-between font-serif text-2xl border-t border-tablo-border pt-3 mt-1">
              <span>Deposit now <span className="text-xs text-light-ink/80 font-sans">(incl. GST)</span></span>
              <span data-testid="checkout-deposit-amount">₹{depositWithGst.toLocaleString("en-IN")}</span>
            </div>
            <p className="text-[11px] text-light-ink/70">Balance ₹{(grand - depositWithGst).toLocaleString("en-IN")} payable at the venue.</p>
          </div>
        );
      })()}

      {/* Company GST invoice block */}
      <div className="tablo-card p-4 space-y-3" data-testid="checkout-billing-card">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={forCompany}
            onChange={(e) => setForCompany(e.target.checked)}
            className="w-4 h-4 accent-ink"
            data-testid="checkout-for-company"
          />
          <span className="text-sm font-medium">I'm booking on behalf of a company (GST invoice)</span>
        </label>
        {forCompany && (
          <div className="space-y-2 pt-2 border-t border-tablo-border">
            <input
              type="text"
              value={billing.company_name}
              onChange={(e) => setBilling({ ...billing, company_name: e.target.value })}
              placeholder="Company name (as on GST certificate)"
              className="w-full bg-white border border-tablo-border rounded-lg px-3 py-2 text-sm"
              data-testid="checkout-company-name"
            />
            <input
              type="text"
              value={billing.gstin}
              onChange={(e) => setBilling({ ...billing, gstin: e.target.value.toUpperCase().slice(0, 15) })}
              placeholder="GSTIN (15 chars)"
              className="w-full bg-white border border-tablo-border rounded-lg px-3 py-2 text-sm uppercase tracking-wider"
              maxLength={15}
              data-testid="checkout-gstin"
            />
            <textarea
              rows={2}
              value={billing.billing_address}
              onChange={(e) => setBilling({ ...billing, billing_address: e.target.value })}
              placeholder="Registered billing address"
              className="w-full bg-white border border-tablo-border rounded-lg px-3 py-2 text-sm"
              data-testid="checkout-billing-address"
            />
            <p className="text-[11px] text-light-ink/70">GST invoice will be emailed to you within 48 hrs of the meal.</p>
          </div>
        )}
      </div>

      <div className="bg-amber-soft/20 border border-amber-soft/40 rounded-xl p-4 flex gap-2 text-sm" data-testid="checkout-policy-box">
        <Shield className="w-4 h-4 text-amber mt-0.5" strokeWidth={1.6}/>
        <div>
          <p className="font-medium">Cancellation policy</p>
          <p className="text-light-ink text-xs">4 hrs+ before slot: 100% refund · 1–4 hrs: 50% refund · &lt;1 hr or no-show: no refund.</p>
        </div>
      </div>

      <Button onClick={pay} disabled={paying} className="w-full tablo-btn-primary" data-testid="checkout-pay-btn">
        {paying ? "Processing payment…" : `Pay ₹${(booking.deposit_amount_inr || 0).toLocaleString("en-IN")} · ${rzpEnabled ? "Razorpay" : "Mock checkout"}`}
        {!paying && <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2}/>}
      </Button>
      <p className="text-[11px] text-light-ink/70 text-center">{rzpEnabled ? "Powered by Razorpay · UPI, cards, netbanking, wallets." : "Demo mode — Razorpay keys not configured. Deposit is simulated."}</p>
    </div>
  );
}
